import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { NotificationDto, Paginated } from '@eombor/contracts';
import { notificationApi, type NotificationQuery } from '../api/notification.api.js';

/**
 * Bildirishnoma so'rovlari bitta joyda: kalitlar ham shu yerda turadi,
 * shuning uchun "o'qildi" belgisidan keyin ham ro'yxat, ham yon paneldagi
 * son bir xil yangilanadi.
 */
export const notificationKeys = {
  all: ['notification'] as const,
  list: (query: NotificationQuery) => ['notification', 'list', query] as const,
};

/**
 * O'qilmagan bildirishnomalar soni — yon paneldagi belgining manbasi.
 *
 * Uzoq intervalda o'zi yangilanadi: xabar boshqa xodimning amalidan tug'iladi,
 * ya'ni sahifa o'zgarmasa ham paydo bo'lishi mumkin. 60 soniya — smena ichida
 * sezilarli kechikish emas, lekin serverga ham yuk emas.
 */
export const UNREAD_REFETCH_MS = 60_000;

export const useUnreadNotifications = (): UseQueryResult<Paginated<NotificationDto>> => {
  const query: NotificationQuery = { onlyUnread: true, limit: 20 };
  return useQuery({
    queryKey: notificationKeys.list(query),
    queryFn: () => notificationApi.list(query),
    refetchInterval: UNREAD_REFETCH_MS,
  });
};

/**
 * Bitta turdagi o'qilmagan xabarlar SONI — yon paneldagi belgi.
 *
 * `total` olinadi, `unread` emas: `onlyUnread: true` bilan ikkovi bir xil
 * bo'ladi va mok rejimda ham shu ishlaydi.
 *
 * `enabled` — ruxsati yo'q xodim uchun so'rov umuman ketmaydi.
 */
export const useUnreadCountOfKind = (kind: string, enabled = true): number => {
  const query: NotificationQuery = { onlyUnread: true, kind, limit: 1 };
  const result = useQuery({
    queryKey: notificationKeys.list(query),
    queryFn: () => notificationApi.list(query),
    refetchInterval: UNREAD_REFETCH_MS,
    enabled,
  });
  return result.data?.total ?? 0;
};

/**
 * Qaysi HUJJATLAR o'qilmagan — ro'yxatdagi belgini qo'yish uchun.
 *
 * `id` lar to'plami qaytadi, son emas: kartochka "menda ish bor" degan
 * belgini o'z hujjati uchun ko'rsatishi kerak, umumiy son esa buni
 * ayta olmaydi.
 *
 * `useUnreadCountOfKind` bilan BIR XIL kalitdan foydalanilmaydi: bu yerda
 * ro'yxat kerak (`limit` katta), u yerda faqat `total`. Ikkovini bitta
 * so'rovga birlashtirish sonni ro'yxat hajmiga bog'lab qo'yardi.
 */
export const useUnreadNotificationEntities = (
  kind: string,
  entityType: string,
  enabled = true,
): ReadonlySet<string> => {
  const query: NotificationQuery = { onlyUnread: true, kind, limit: 100 };
  const result = useQuery({
    queryKey: notificationKeys.list(query),
    queryFn: () => notificationApi.list(query),
    refetchInterval: UNREAD_REFETCH_MS,
    enabled,
  });
  return useMemo(
    () =>
      new Set(
        (result.data?.items ?? [])
          .filter((row) => row.entityType === entityType && row.entityId !== null)
          .map((row) => row.entityId as string),
      ),
    [result.data, entityType],
  );
};

/**
 * Hujjat ochilganda uning xabarlarini o'qilgan deb belgilaydi.
 *
 * Kalit xabar `id` si emas, HUJJAT: ekran qaysi xabar ekanini bilmaydi.
 */
export const useMarkEntityRead = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      notificationApi.markEntityRead(entityType, entityId),
    onSuccess: () => void client.invalidateQueries({ queryKey: notificationKeys.all }),
  });
};

export const useMarkAllNotificationsRead = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => notificationApi.markAllRead(),
    onSuccess: () => void client.invalidateQueries({ queryKey: notificationKeys.all }),
  });
};
